import {css, html, LitElement} from 'lit';
import {customElement, property} from 'lit/decorators';
import {Resource} from './resource';
import {ifDefined} from 'lit-html/directives/if-defined.js';
import {containerStyles, moduleSpacingStyles, normalizeStyles} from '@webs/components-library/theming';
import {repeat} from 'lit-html/directives/repeat.js';

@customElement('webs-client-cases-filtered')
export class ClientCasesFiltered extends LitElement {

  private resources: Resource[] = [];
  private totalCount = 0;
  private currentPage = 1;

  static styles = [
    normalizeStyles,
    containerStyles,
    moduleSpacingStyles,
    css`
      webs-filters-dropdown {
        display: block;
        padding: 1.6rem 0;
      }

      webs-card-resource{
        --card-justify-content: unset;
        --content-height: unset;
      }

      webs-highlight-general {
        display: block;
        margin: 1.6rem 0;
      }

      webs-listing-resources webs-card-resource:nth-of-type(2){
        margin-top: 0;
      }

      @media only screen and (min-width: 768px) {
        webs-filters-dropdown {
          padding: 2rem 0 3rem;
        }
      }

      @media only screen and (min-width: 992px) {
        webs-highlight-general {
          margin: 4rem 0;
        }

        webs-listing-resources webs-card-resource:nth-of-type(4){
        margin-top: 0;
      }
      }
    `
  ];

  @property({attribute: 'space-top'})
  spaceTop = '0';

  @property({attribute: 'space-bottom'})
  spaceBottom = 'm';

  @property()
  id: string;

  @property()
  url: string;

  @property({attribute: 'limit', type: Number})
  limit = 8;

  @property({type: Number})
  offset = 0;

  @property({type: Boolean})
  alternative: boolean;

  @property({attribute: 'highlight-button-text'})
  highlightButtonText = 'Read client case';

  protected render(): unknown {
    let totalPages = 0;
    if (this.totalCount > 0 && this.limit > 0) {
      totalPages = Math.ceil(this.totalCount / this.limit);
    }
    return html`
      <section id="${ifDefined(this.id)}"
               class="m-client-cases-filtered ${this.spaceTop ? 'module_top-padding-' + this.spaceTop : ''} ${this.spaceBottom ? 'module_bottom-padding-' + this.spaceBottom : ''}">
        <div class="container">
          <webs-filters-dropdown url="${this.url}" limit="${this.limit}" start-at="${this.offset}"
                                 @resourceschange="${this.refreshCases}" @filteradd="${this.resetPagination}">
            <slot></slot>
          </webs-filters-dropdown>
        </div>

        ${this.resources && this.resources.length > 0 ? html`
          ${this.constructResults()}

          ${totalPages > 1 ? html`
            <webs-pagination position="center" total-pages="${totalPages}" page="${this.currentPage}"
                             @pagechange="${this.onPageChange}"></webs-pagination>
          ` : ''}
        ` : ''}
      </section>
    `;
  }

  private constructResults(): unknown {
    const simpleResources: Resource[] = this.resources.slice(1);

      const highlighted = html`
        <webs-highlight-general slot="resource"
                                tag="${ifDefined(this.resources[0].tag)}"
                                logo-src="${ifDefined(this.resources[0].logo)}">

          ${this.resources[0].image ? html`
            <webs-media slot="media" src="${this.resources[0].image}" alt="${this.resources[0].title}" width="590"
                        height="80%"
                        type="cover"></webs-media>
          ` : ''}

          ${this.resources[0].href ? html`
            <webs-button slot="action" href="${this.resources[0].href}"
                         text="${this.highlightButtonText}"></webs-button>
          ` : ''}

          ${this.resources[0].quoter || this.resources[0].position ? html`
            <webs-quote slot="description"
                        quote="${ifDefined(this.resources[0].description)}"
                        avatar="${ifDefined(this.resources[0].avatar)}"
                        quoter="${ifDefined(this.resources[0].quoter)}"
                        extra-info="${ifDefined(this.resources[0].position)}"></webs-quote>
          ` : ''}
        </webs-highlight-general>
      `;

    const lastHalf = html`
      <webs-listing-resources space-top="0" space-bottom="0">
        ${repeat(simpleResources, (item, index) => {
      return html`
            <webs-card-resource size="${index === 4 ? '12' : '6'}" md="${index === 4 ? '6' : '3'}" slot="resource"
                                tag="${ifDefined(item.tag)}"
                                title="${ifDefined(item.title)}"
                                image="${ifDefined(item.image)}"
                                logo-src="${ifDefined(item.logo)}"
                                href="${ifDefined(item.href)}"
                                height="${index === 4 ? '57.433%' : '118.06%' }">
            </webs-card-resource>
          `;
    })}
      </webs-listing-resources>
    `;

    return html`${highlighted} ${lastHalf}`;
  }

  private resetPagination(): void {
    this.currentPage = 1;
    this.offset = 0;
    this.requestUpdate();
  }

  private onPageChange(e: { detail: number }): void {
    if (this.currentPage == e.detail) {
      return;
    }
    this.currentPage = e.detail;
    this.offset = (e.detail - 1) * this.limit;
    this.requestUpdate();
  }

  private refreshCases(event: CustomEvent): void {
    const data = event.detail.objects;
    this.totalCount = event.detail.totalCount;
    console.log(data);
    this.resources = [];
    for (let i = 0; i < data.length; i++) {
      const tag = (typeof data[i].values['filter_tag'] === 'object') ? data[i].values['filter_tag'].name : data[i].values['filter_tag'];

      this.resources.push({
        tag: tag,
        title: data[i].values['title'],
        description: data[i].values['short_description'],
        image: data[i].values['thumbnail'] ? data[i].values['thumbnail'].url : undefined,
        href: data[i].values['link'],
        logo: data[i].values['logo'] ? data[i].values['logo'].url : undefined,
        avatar: data[i].values['quoter_avatar'] ? data[i].values['quoter_avatar'].url : undefined,
        quoter: data[i].values['quoter_name'] ? data[i].values['quoter_name'] : undefined,
        position: data[i].values['quoter_position'] ? data[i].values['quoter_position'] : undefined
      });
    }

    this.requestUpdate();
  }
}
